// visitenkarteVerbindung.ts — die Firma einer Visitenkarte (oder Abrechnung)
// kommt in „Verbindung zu Unternehmen" (`rel_personFirma`) der Personen-Anlage.
//
// Plan 2026-09-30-ocr-scan-kontaktanlage, P2–P7 und P9:
//
//   P2  Firmenname und -anschrift erzeugen einen Eintrag (Rolle = Position).
//       Angelegt wird die Firma erst beim Speichern, nicht schon im Dialog —
//       bis 09/2026 legte der Scan sie sofort per POST an, und nach einem
//       Abbruch blieb eine verwaiste Firma zurück (P9).
//   P3  Gibt es die Firma schon, trägt der Eintrag ihre `contactId`.
//   P4  Sonst ist die Referenz `manual` — die Schnellanlage („Neu") ist mit den
//       Firmendaten der Karte vorbefüllt.
//   P5  Ein schon angefangener, aber LEERER Eintrag wird gefüllt, statt einen
//       zweiten anzulegen. Die Status-Folgefelder (`UnternehmenInline`) und die
//       Liste weiter unten teilen sich `rel_personFirma` — das gilt also für
//       beide.
//   P6  Steht dort schon eine ANDERE Firma, wird angehängt, nie überschrieben.
//   P7  Der Arbeitgeber wird aus der Verbindung abgeleitet, nicht getrennt
//       gesetzt (utils/arbeitgeberVerbindung.ts): Eine bestehende Firma leitet
//       die Maske über `arbeitgeberVorschlag` ab. Eine NEUE Firma ohne Id kann
//       das nicht — sie wird, solange noch kein Arbeitgeber gewählt ist, als
//       neu eingetippter Arbeitgeber geführt (`autoAusArbeitgeber`), genau wie
//       es die Arbeitgeber-Schnellanlage tut. Dann legt der Arbeitgeber-Weg
//       sie beim Speichern EINMAL an und reicht die Rolle nach.

import type { ContactRef, PersonFirmaItem } from "../components/relationsInline/types";
import { uid } from "../components/relationsInline/types";
import type { VerantRole } from "../components/verantwortlichkeit/VerantwortlichkeitInput";
import { firmenKern } from "./visitenkarteFirmensuche";

const text = (s: unknown): string => String(s ?? "").trim();

/** Ein Eintrag, in dem noch keine Firma steht (angefangen, aber leer). */
export function istLeererFirmenEintrag(it: PersonFirmaItem): boolean {
  const ref = it?.ref;
  if (!ref) return true;
  return !ref.contactId && !text(ref.lastName) && !text(ref.displayName);
}

const firmaVon = (it: PersonFirmaItem): ContactRef | null | undefined =>
  it.verant?.isExtern ? it.verant?.externalCompany : it.ref;

/** Steht dieselbe Firma schon im Eintrag (Id oder — bei neuen — Name)? */
function gleicheFirma(it: PersonFirmaItem, firma: ContactRef): boolean {
  const vorhanden = firmaVon(it);
  if (!vorhanden) return false;
  if (firma.contactId) return Number(vorhanden.contactId) === Number(firma.contactId);
  if (vorhanden.contactId) return false;
  const a = firmenKern(text(vorhanden.lastName || vorhanden.displayName));
  return !!a && a === firmenKern(text(firma.lastName));
}

const mitRolle = (roles: VerantRole[], position?: string): VerantRole[] => {
  const p = text(position);
  if (!p) return roles;
  const schonDa = roles.some(
    (r) => r.kind === "mensch" && text(r.verantwortlichkeit).toLowerCase() === p.toLowerCase(),
  );
  return schonDa ? roles : [...roles, { kind: "mensch", verantwortlichkeit: p }];
};

export type VerbindungErgebnis = {
  items: PersonFirmaItem[];
  /** Die Firma ist neu und wird als neu eingetippter Arbeitgeber geführt (P7). */
  alsNeuerArbeitgeber: boolean;
};

/**
 * Trägt die Firma der Karte in `rel_personFirma` ein.
 *
 * @param firma  bestehend (`contactId`) oder neu (`manual`, vorbefüllt)
 * @param position  Position von der Karte → Rolle „Mensch fürs Unternehmen"
 * @param arbeitgeberFrei  Es ist noch kein Arbeitgeber gewählt (weder
 *   bestehend noch als Neuanlage) — nur dann darf eine NEUE Firma als
 *   Arbeitgeber geführt werden.
 */
export function firmaInVerbindungen(args: {
  items: PersonFirmaItem[];
  firma: ContactRef;
  position?: string;
  arbeitgeberFrei: boolean;
}): VerbindungErgebnis {
  const { items, position, arbeitgeberFrei } = args;
  const firma: ContactRef = { ...args.firma, type: "Firma" };
  const neu = !firma.contactId;

  // Dieselbe Firma steht schon da (etwa zweimal gescannt): nur die Rolle ergänzen.
  const gleich = items.findIndex((it) => gleicheFirma(it, firma));
  if (gleich >= 0) {
    const it = items[gleich];
    const roles = mitRolle(it.verant?.roles ?? [], position);
    if (roles === (it.verant?.roles ?? [])) return { items, alsNeuerArbeitgeber: false };
    const next = items.map((x, i) =>
      i === gleich ? { ...x, verant: { isExtern: false, externalCompany: null, ...(x.verant ?? {}), roles } } : x,
    );
    return { items: next, alsNeuerArbeitgeber: false };
  }

  const alsNeuerArbeitgeber = neu && arbeitgeberFrei;
  const eintrag = (id: string, vorher?: PersonFirmaItem): PersonFirmaItem => ({
    ...(vorher ?? {}),
    id,
    ref: firma,
    verant: {
      isExtern: false,
      externalCompany: null,
      roles: mitRolle([], position),
      // Die Karte belegt genau das: Die Person arbeitet dort.
      istMenschImUnternehmen: true,
      istMenschImUnternehmenManuell: true,
    },
    ...(alsNeuerArbeitgeber ? { autoAusArbeitgeber: true } : {}),
  });

  const leer = items.findIndex(istLeererFirmenEintrag);
  if (leer >= 0) {
    return {
      items: items.map((x, i) => (i === leer ? eintrag(x.id, x) : x)),
      alsNeuerArbeitgeber,
    };
  }
  return { items: [...items, eintrag(uid())], alsNeuerArbeitgeber };
}
